import React, { MouseEvent, ReactNode } from 'react';
import parse, { domToReact, HTMLReactParserOptions } from 'html-react-parser';
import { ArrowList, OrderList } from 'components/_new/List';
import { Tip } from 'components/userPanel/sessions/components/Tip';
import { Link, LinkExtendProps } from 'components/_new/Link';
import { LinkDoc } from 'components/userPanel/sessions/components/LinkDoc';
import { Blue } from 'components/userPanel/sessions/components/Blue';
import { BlueQuestions } from 'components/userPanel/sessions/components/BlueQuestions';
import { Pink } from 'components/userPanel/sessions/components/Pink';
import { PinkQuestions } from 'components/userPanel/sessions/components/PinkQuestions';
import { MessageBox } from 'components/_new/MessageBox';
import { scrollElementToById } from './scroll';

const Blank = ({ message }: { message: ReactNode }) => {
	return <MessageBox variant="blank" message={message} />;
};

export type ContentParserOptions = {
	parserOptions?: HTMLReactParserOptions;
	handlers?: Record<string, (...args: any) => void>;
	replaces?: Record<string, any>;
	disableFormat?: boolean;
	forceTargetBlank?: boolean;
};

export const contentParser = (options?: ContentParserOptions) => {
	const { parserOptions, handlers, replaces, disableFormat, forceTargetBlank } = {
		parserOptions: {},
		handlers: {},
		replaces: {},
		disableFormat: false,
		forceTargetBlank: false,
		...options,
	};

	const replace = (domNode: any) => {
		const domNodeName = String(domNode.name).toLowerCase();

		if (parserOptions?.replace) {
			const replaced = parserOptions.replace(domNode);
			if (replaced !== undefined) {
				return replaced;
			}
		}

		if (!disableFormat) {
			if (domNodeName === 'div' && domNode.attribs?.tip !== undefined && domNode.attribs.tip === '') {
				return <Tip message={domToReact(domNode.children, finalParserOptions)} />;
			}
			if (domNodeName === 'div' && domNode.attribs?.blue !== undefined && domNode.attribs.blue === '') {
				return <Blue message={domToReact(domNode.children, finalParserOptions)} />;
			}
			if (
				domNodeName === 'div' &&
				domNode.attribs?.blue_questions !== undefined &&
				domNode.attribs.blue_questions === ''
			) {
				return <BlueQuestions message={domToReact(domNode.children, finalParserOptions)} />;
			}
			if (domNodeName === 'div' && domNode.attribs?.pink !== undefined && domNode.attribs.pink === '') {
				return <Pink message={domToReact(domNode.children, finalParserOptions)} />;
			}
			if (
				domNodeName === 'div' &&
				domNode.attribs?.pink_questions !== undefined &&
				domNode.attribs.pink_questions === ''
			) {
				return <PinkQuestions message={domToReact(domNode.children, finalParserOptions)} />;
			}
			if (domNodeName === 'ul') {
				const list = <ArrowList>{domToReact(domNode.children, finalParserOptions)}</ArrowList>;
				if (domNode.attribs?.blue !== undefined && domNode.attribs.blue === '') {
					return <Blue message={list} />;
				}
				if (domNode.attribs?.blue_questions !== undefined && domNode.attribs.blue_questions === '') {
					return <BlueQuestions message={list} />;
				}
				if (domNode.attribs?.pink !== undefined && domNode.attribs.pink === '') {
					return <Pink message={list} />;
				}
				if (domNode.attribs?.pink_questions !== undefined && domNode.attribs.pink_questions === '') {
					return <PinkQuestions message={list} />;
				}
				return <Blank message={list} />;
			}
			if (domNodeName === 'ol') {
				return <OrderList>{domToReact(domNode.children, finalParserOptions)}</OrderList>;
			}
		}
		if (domNodeName === 'a') {
			const href = String(domNode.attribs.href || '');
			const isArticleLink =
				(domNode.attribs?.article !== undefined && domNode.attribs.article !== '') ||
				href.includes('/knowledge-base/article/');
			const linkProps: LinkExtendProps = {
				to: href,
				onClick: (event: MouseEvent<HTMLAnchorElement>) => {
					if (
						domNode.attribs?.openautoreflection !== undefined &&
						domNode.attribs.openautoreflection === ''
					) {
						scrollElementToById('scrollWrapperRef');
						scrollElementToById('pageScrollWrapperRef');
						handlers?.openAutoreflectionDialog?.();
						// event.preventDefault();
					}

					if (domNode.attribs?.scrolltotop !== undefined && domNode.attribs.scrolltotop === '') {
						scrollElementToById('scrollWrapperRef');
						scrollElementToById('pageScrollWrapperRef');
						// event.preventDefault();
					}
					if (isArticleLink) {
						const lastLinkPart = href.split('/').pop()?.split('-')[0];
						const articleId = parseInt(domNode.attribs.article || lastLinkPart, 10);
						if (articleId && !Number.isNaN(articleId)) {
							event.preventDefault();
							handlers?.onArticleLinkClick?.(articleId);
						}
					}
				},
			};

			if (forceTargetBlank) {
				linkProps.target = '_blank';
				linkProps.rel = 'noopener noreferrer';
			}

			const isTextLink = domNode.children.length === 1 && domNode.children[0].type === 'text';
			if (isArticleLink && (isTextLink || !disableFormat)) {
				return (
					<span className="inline">
						<LinkDoc {...linkProps} to={isArticleLink ? '#' : linkProps.to}>
							{domToReact(domNode.children, finalParserOptions)}
						</LinkDoc>
						{isArticleLink && (
							<Link
								to={linkProps.to}
								target="_blank"
								icon="external-link-alt"
								iconStyle="solid"
								iconOnly
							/>
						)}
					</span>
				);
			}
			if (isTextLink) {
				return (
					<Link target={domNode.attribs?.target} {...linkProps}>
						{domToReact(domNode.children, finalParserOptions)}
					</Link>
				);
			}
			return domNode;
		}
		if (parserOptions?.replace) {
			return parserOptions.replace(domNode);
		}
		return domNode;
	};

	const finalParserOptions = { ...parserOptions, replace };

	return {
		parse: (_content: string) => {
			let content = _content;

			const replaceEntries = Array.from(Object.entries(replaces || {}));
			if (replaceEntries.length > 0) {
				content = replaceEntries.reduce(
					(current, [name, replacement]) => current.replaceAll(`{{${name}}}`, String(replacement)),
					content
				);
			}

			return parse(content, finalParserOptions);
		},
	};
};
